import { useMemo } from "react";
import { MDBSelect } from "mdb-react-ui-kit";
import { useTranslation } from "react-i18next";
import { getCountryOptions, normalizeCountryCode } from "../utils/countryCode";

/**
 * Country picker that always stores an ISO 3166-1 alpha-2 code ("DE").
 *
 * Drop-in for the old free-text country inputs: with `datapath` set, onChange
 * receives the same `{ target: { attributes: { datapath }, value } }` shape the
 * forms' handleChange functions already read.
 *
 * Legacy values ("Deutschland", "germany") are mapped to their code for
 * display; MDBSelect then reports that code through onChange on mount, so
 * saving the form writes the clean value back.
 */
export default function CountrySelect({ value, onChange, datapath, label, invalid, inputClassName, required, ...rest }) {
	const { t } = useTranslation();

	// The placeholder must stay the first entry: MDBSelect falls back to the
	// first option whenever `value` matches nothing, and that fallback is only
	// displayed, never reported through onChange (NET-48).
	const data = useMemo(() => [
		{ text: t("Select"), value: "", disabled: true, defaultSelected: true },
		...getCountryOptions(t)
	], [t]);

	const code = normalizeCountryCode(value, t);

	const handleChange = (option) => {
		const next = option?.value;
		if (!next || next === value) return;
		if (datapath) {
			onChange?.({ target: { attributes: { datapath: { value: datapath } }, value: next, type: "select-one" } });
		} else {
			onChange?.(next);
		}
	};

	const select = (
		<MDBSelect
			search
			data={data}
			value={code}
			label={label ?? t("Country")}
			inputClassName={[inputClassName, invalid ? "is-invalid" : ""].filter(Boolean).join(" ")}
			onChange={handleChange}
			required={required}
			{...rest}
		/>
	);

	if (!required) return select;

	// MDBSelect's input is read-only, and browsers skip read-only fields in
	// native form validation — this invisible twin keeps `required` working.
	return (
		<div className="position-relative">
			{select}
			<input
				tabIndex={-1}
				aria-hidden="true"
				required
				value={code}
				onChange={() => {}}
				style={{ position: "absolute", left: 0, bottom: 0, width: "100%", height: 1, opacity: 0, border: 0, padding: 0, pointerEvents: "none" }}
			/>
		</div>
	);
}
